// Guide §12.1 — Subscription state machine as pure functions shared by app, API and Jobs.
// State is *derived* from time on every read (so a late Job never shows a wrong state);
// the 10-minute Job then persists the same result and sends notices.
import type { FreeReason, GrantType, PaidPlan, Plan, SubStatus } from '../enums';
import { GRACE_DAYS, PERIOD_DAYS, TRIAL_DAYS } from '../pricing';
import { addDays } from '../time';

export interface SubscriptionSnapshot {
  plan: Plan;
  status: SubStatus;
  freeReason?: FreeReason | null;
  previousPlan?: Plan | null;
  trialEndsAt?: Date | null;
  currentPeriodStart?: Date | null;
  currentPeriodEnd?: Date | null;
  graceEndsAt?: Date | null;
  cancelAtPeriodEnd: boolean;
  scheduledPlan?: Plan | null;
  extraShops: number;
  grantType?: GrantType | null;
  grantEndsAt?: Date | null;
  lapsedAt?: Date | null;
}

export type SubReason =
  | 'payment'
  | 'trial_start'
  | 'trial_expiry'
  | 'period_end'
  | 'grace_expiry'
  | 'cancel_effective'
  | 'upgrade'
  | 'admin_grant'
  | 'downgrade_applied';

export const FREE_SUBSCRIPTION: SubscriptionSnapshot = {
  plan: 'free',
  status: 'free',
  cancelAtPeriodEnd: false,
  extraShops: 0,
};

function toFree(s: SubscriptionSnapshot, reason: FreeReason, at: Date): SubscriptionSnapshot {
  return {
    ...s,
    previousPlan: s.plan === 'free' ? (s.previousPlan ?? null) : s.plan,
    plan: 'free',
    status: 'free',
    freeReason: reason,
    cancelAtPeriodEnd: false,
    scheduledPlan: null,
    graceEndsAt: null,
    lapsedAt: at,
  };
}

/**
 * Returns the state the subscription *is in* at `now`. Applies at most the transitions that time alone causes.
 * Loops because a long-dead Job may need several hops (active → past_due → free).
 */
export function resolveSubscription(s: SubscriptionSnapshot, now: Date): SubscriptionSnapshot {
  let cur = s;
  for (let i = 0; i < 3; i++) {
    const next = stepSubscription(cur, now);
    if (next === cur) return cur;
    cur = next;
  }
  return cur;
}

function stepSubscription(s: SubscriptionSnapshot, now: Date): SubscriptionSnapshot {
  if (s.status === 'trial' && s.trialEndsAt && now >= s.trialEndsAt) return toFree(s, 'trial_ended', s.trialEndsAt);
  if (s.status === 'active' && s.currentPeriodEnd && now >= s.currentPeriodEnd) {
    if (s.cancelAtPeriodEnd) return toFree(s, 'cancelled', s.currentPeriodEnd);
    return { ...s, status: 'past_due', graceEndsAt: addDays(s.currentPeriodEnd, GRACE_DAYS) };
  }
  if (s.status === 'past_due' && s.graceEndsAt && now >= s.graceEndsAt) return toFree(s, 'lapsed', s.graceEndsAt);
  return s;
}

/** Which time-driven transition (if any) turned `before` into `after`? Used for SubscriptionEvent.reason. */
export function transitionReason(before: SubscriptionSnapshot, after: SubscriptionSnapshot): SubReason | null {
  if (before.status === after.status && before.plan === after.plan) return null;
  if (before.status === 'trial' && after.status === 'free') return 'trial_expiry';
  if (before.status === 'active' && after.status === 'past_due') return 'period_end';
  if (before.status === 'active' && after.status === 'free') return 'cancel_effective';
  if (before.status === 'past_due' && after.status === 'free') return 'grace_expiry';
  return null;
}

/** First paid shop + unused trial → start the 15-day trial (Guide §9.2 step 4). */
export function startTrial(s: SubscriptionSnapshot, now: Date): SubscriptionSnapshot {
  return {
    ...s,
    plan: 'free',
    status: 'trial',
    freeReason: null,
    trialEndsAt: addDays(now, TRIAL_DAYS),
  };
}

export interface PaymentActivation {
  next: SubscriptionSnapshot;
  reason: SubReason;
  /** Free SMS points to grant for the new period (0 when this is only an upgrade) */
  grantsFreePoints: boolean;
  /** Suspended staff should be re-activated (free → paid with a staff-capable plan) */
  reactivateStaff: boolean;
}

/**
 * Apply a successful subscription payment for plan `plan` (Guide §12.1 table):
 *  trial   → active, period end = trialEndsAt + 30d (no day lost, Bible 6.2)
 *  active  → early renewal extends from the *old end*; a different, higher plan = upgrade (same end date)
 *  past_due→ new period starts at the old end
 *  free    → now + 30d
 */
export function applySubscriptionPayment(
  s: SubscriptionSnapshot,
  plan: PaidPlan,
  now: Date,
  opts: { upgrade?: boolean; /** length of the paid term in days (default one month) */ days?: number } = {},
): PaymentActivation {
  const days = opts.days ?? PERIOD_DAYS;
  const base: SubscriptionSnapshot = {
    ...s,
    plan,
    status: 'active',
    freeReason: null,
    graceEndsAt: null,
    cancelAtPeriodEnd: false,
    lapsedAt: null,
    grantType: null,
    grantEndsAt: null,
  };
  const wasFree = s.status === 'free';
  const reactivateStaff = wasFree;
  switch (s.status) {
    case 'trial': {
      const start = s.trialEndsAt && s.trialEndsAt > now ? s.trialEndsAt : now;
      return {
        next: { ...base, trialEndsAt: null, currentPeriodStart: start, currentPeriodEnd: addDays(start, days), scheduledPlan: null },
        reason: 'payment',
        grantsFreePoints: true,
        reactivateStaff,
      };
    }
    case 'active': {
      if (opts.upgrade) {
        // immediate plan change, end date unchanged
        return { next: { ...base, currentPeriodStart: s.currentPeriodStart, currentPeriodEnd: s.currentPeriodEnd }, reason: 'upgrade', grantsFreePoints: false, reactivateStaff: false };
      }
      const start = s.currentPeriodEnd ?? now;
      const applied = s.scheduledPlan && s.scheduledPlan !== 'free' && s.scheduledPlan !== 'enterprise' ? (s.scheduledPlan as PaidPlan) : plan;
      return {
        next: { ...base, plan: applied, currentPeriodStart: start, currentPeriodEnd: addDays(start, days), scheduledPlan: null },
        reason: 'payment',
        grantsFreePoints: true,
        reactivateStaff: false,
      };
    }
    case 'past_due': {
      const start = s.currentPeriodEnd ?? now;
      return {
        next: { ...base, currentPeriodStart: start, currentPeriodEnd: addDays(start, days), scheduledPlan: null },
        reason: 'payment',
        grantsFreePoints: true,
        reactivateStaff: false,
      };
    }
    default: {
      return {
        next: { ...base, currentPeriodStart: now, currentPeriodEnd: addDays(now, days), scheduledPlan: null },
        reason: 'payment',
        grantsFreePoints: true,
        reactivateStaff,
      };
    }
  }
}

/** Admin complimentary plan (Bible 17.4): active until grantEndsAt, then normal rules apply. */
export function applyAdminGrant(
  s: SubscriptionSnapshot,
  plan: PaidPlan,
  grantType: GrantType,
  grantEndsAt: Date,
  now: Date,
): SubscriptionSnapshot {
  return {
    ...s,
    plan,
    status: 'active',
    freeReason: null,
    graceEndsAt: null,
    cancelAtPeriodEnd: false,
    lapsedAt: null,
    grantType,
    grantEndsAt,
    currentPeriodStart: now,
    currentPeriodEnd: grantEndsAt,
    scheduledPlan: null,
  };
}

export function requestCancel(s: SubscriptionSnapshot): SubscriptionSnapshot {
  if (s.status !== 'active') return s;
  return { ...s, cancelAtPeriodEnd: true };
}
export function undoCancel(s: SubscriptionSnapshot): SubscriptionSnapshot {
  if (s.status !== 'active') return s;
  return { ...s, cancelAtPeriodEnd: false };
}
export function scheduleDowngrade(s: SubscriptionSnapshot, to: PaidPlan): SubscriptionSnapshot {
  return { ...s, scheduledPlan: to };
}

/** Days left until the thing that ends the current state (for banners) */
export function daysLeft(s: SubscriptionSnapshot, now: Date): number | null {
  const end =
    s.status === 'trial' ? s.trialEndsAt : s.status === 'active' ? s.currentPeriodEnd : s.status === 'past_due' ? s.graceEndsAt : null;
  if (!end) return null;
  return Math.max(0, Math.ceil((+end - +now) / 86_400_000));
}

export type BannerKind = 'trial' | 'renew_soon' | 'cancel_scheduled' | 'past_due' | 'free' | 'none';
export interface BannerInfo {
  kind: BannerKind;
  daysLeft?: number;
  endsAt?: Date | null;
}

/** Bible 6.4 table "যা দেখায়". Renewal banner starts 3 days before the end. */
export function bannerFor(s: SubscriptionSnapshot, now: Date): BannerInfo {
  switch (s.status) {
    case 'trial':
      return { kind: 'trial', daysLeft: daysLeft(s, now) ?? 0, endsAt: s.trialEndsAt };
    case 'active': {
      const d = daysLeft(s, now) ?? 99;
      if (s.cancelAtPeriodEnd) return { kind: 'cancel_scheduled', daysLeft: d, endsAt: s.currentPeriodEnd };
      if (d <= 3) return { kind: 'renew_soon', daysLeft: d, endsAt: s.currentPeriodEnd };
      return { kind: 'none' };
    }
    case 'past_due':
      return { kind: 'past_due', daysLeft: daysLeft(s, now) ?? 0, endsAt: s.graceEndsAt };
    default:
      return { kind: 'free' };
  }
}
